import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {IUnlockChoiceOption, UnlockChoiceOptionId} from "../../../types/IUnlockChoiceOption";
import {UnlockChoiceModalOptionIcon} from "./UnlockChoiceModalOptionIcon";

interface IProps {
    option: IUnlockChoiceOption;
    inputName: string;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: (optionId: UnlockChoiceOptionId) => void;
    dataTestId?: string;
}

export const UnlockChoiceModalOptionCard = (props: IProps) => {
    const {option, inputName, isSelected, isDisabled, onSelect, dataTestId} = props;

    const isExpanded = isSelected && Boolean(option.description);

    return (
        <label className={isSelected ? selectedCardStyle : cardStyle} data-testid={dataTestId}>
            <input
                type="radio"
                name={inputName}
                value={option.id}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => onSelect(option.id)}
                className={radioInputStyle}
            />

            {option.badge && (
                <span className={option.badge.color === "green" ? greenBadgeStyle : beigeBadgeStyle}>
                    <Text as="span" variant="info_txt_1" color="var(--colors-secondary)" noWrap>
                        {option.badge.text}
                    </Text>
                </span>
            )}

            <span className={rowStyle}>
                <span className={bodyStyle}>
                    <span className={iconTileStyle}>
                        <UnlockChoiceModalOptionIcon icon={option.icon} />
                    </span>

                    <span className={textsStyle}>
                        <span className={isExpanded ? expandedTitleSlotStyle : titleSlotStyle}>
                            <Text as="span" variant="headline_6" color="var(--colors-secondary)">
                                {option.title}
                            </Text>
                        </span>

                        {option.description && (
                            <span className={isExpanded ? expandedDescriptionStyle : descriptionStyle} aria-hidden={!isExpanded}>
                                <span className={descriptionClipStyle}>
                                    <Text as="span" variant="info_txt_1" color="var(--colors-gray-700)" className={descriptionTextStyle}>
                                        {option.description}
                                    </Text>
                                </span>
                            </span>
                        )}
                    </span>
                </span>

                <span className={isExpanded ? expandedRadioSlotStyle : radioSlotStyle} aria-hidden>
                    <span className={isSelected ? selectedRadioRingStyle : radioRingStyle}>{isSelected && <span className={radioDotStyle} />}</span>
                </span>
            </span>
        </label>
    );
};

const motionStyle = css.raw({
    transitionDuration: "250ms",
    transitionTimingFunction: "ease-out",
    _motionReduce: {
        transitionDuration: "0ms"
    }
});

const cardBaseStyle = css.raw({
    ...motionStyle,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: 1,
    cursor: "pointer",
    transitionProperty: "border-color, background-color, box-shadow",
    "&:has(input:focus-visible)": {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    },
    "&:has(input:disabled)": {
        cursor: "default"
    }
});

const cardStyle = css({
    ...cardBaseStyle,
    borderColor: "var(--colors-gray-400)",
    backgroundColor: "var(--colors-white)"
});

const selectedCardStyle = css({
    ...cardBaseStyle,
    borderColor: "var(--colors-secondary)",
    backgroundColor: "var(--colors-background-100)",
    boxShadow: "var(--shadow-deep)"
});

const radioInputStyle = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: "0"
});

const badgeBaseStyle = css.raw({
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-start",
    height: "2.2rem",
    px: 1,
    borderTopLeftRadius: 1,
    borderBottomRightRadius: 0.5
});

const greenBadgeStyle = css({
    ...badgeBaseStyle,
    backgroundColor: "var(--colors-success)"
});

const beigeBadgeStyle = css({
    ...badgeBaseStyle,
    backgroundColor: "var(--colors-background-200)"
});

const rowStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1.4rem",
    px: 2,
    py: 1.5
});

const bodyStyle = css({
    display: "flex",
    flex: "1 1 auto",
    alignItems: "flex-start",
    minWidth: 0,
    columnGap: 0.5
});

const iconTileStyle = css({
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "4rem",
    height: "4rem",
    borderRadius: "50%",
    backgroundColor: "var(--colors-beige-subtle)"
});

const textsStyle = css({
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    minWidth: 0
});

const titleSlotBaseStyle = css.raw({
    ...motionStyle,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    transitionProperty: "min-height"
});

const titleSlotStyle = css({
    ...titleSlotBaseStyle,
    minHeight: "4rem"
});

const expandedTitleSlotStyle = css({
    ...titleSlotBaseStyle,
    minHeight: "2rem",
    md: {
        minHeight: "2.4rem"
    }
});

const descriptionBaseStyle = css.raw({
    ...motionStyle,
    display: "grid",
    transitionProperty: "grid-template-rows, opacity, visibility"
});

const descriptionStyle = css({
    ...descriptionBaseStyle,
    gridTemplateRows: "0fr",
    opacity: 0,
    visibility: "hidden"
});

const expandedDescriptionStyle = css({
    ...descriptionBaseStyle,
    gridTemplateRows: "1fr",
    opacity: 1,
    visibility: "visible"
});

const descriptionClipStyle = css({
    minHeight: 0,
    overflow: "hidden"
});

const descriptionTextStyle = css({
    display: "block",
    pt: 0.5
});

const radioSlotBaseStyle = css.raw({
    ...motionStyle,
    display: "flex",
    flexShrink: 0,
    transitionProperty: "padding-top"
});

const radioSlotStyle = css({
    ...radioSlotBaseStyle,
    pt: 1
});

const expandedRadioSlotStyle = css({
    ...radioSlotBaseStyle,
    pt: 0
});

const radioRingBaseStyle = css.raw({
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "2rem",
    height: "2rem",
    margin: "0.2rem",
    borderWidth: "2px",
    borderStyle: "solid",
    borderRadius: "50%"
});

const radioRingStyle = css({
    ...radioRingBaseStyle,
    borderColor: "var(--colors-gray-700)"
});

const selectedRadioRingStyle = css({
    ...radioRingBaseStyle,
    borderColor: "var(--colors-secondary)"
});

const radioDotStyle = css({
    display: "block",
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
    backgroundColor: "var(--colors-secondary)"
});
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {IUnlockChoiceOption, UnlockChoiceOptionId} from "../../../types/IUnlockChoiceOption";
import {UnlockChoiceModalOptionIcon} from "./UnlockChoiceModalOptionIcon";

interface IProps {
    option: IUnlockChoiceOption;
    inputName: string;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: (optionId: UnlockChoiceOptionId) => void;
    dataTestId?: string;
}

export const UnlockChoiceModalOptionCard = (props: IProps) => {
    const {option, inputName, isSelected, isDisabled, onSelect, dataTestId} = props;

    const isExpanded = isSelected && Boolean(option.description);

    return (
        <label className={isSelected ? selectedCardStyle : cardStyle} data-testid={dataTestId}>
            <input
                type="radio"
                name={inputName}
                value={option.id}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => onSelect(option.id)}
                className={radioInputStyle}
            />

            {option.badge && (
                <span className={option.badge.color === "green" ? greenBadgeStyle : beigeBadgeStyle}>
                    <Text as="span" variant="info_txt_1" color="var(--colors-secondary)" noWrap>
                        {option.badge.text}
                    </Text>
                </span>
            )}

            <span className={rowStyle}>
                <span className={bodyStyle}>
                    <span className={iconTileStyle}>
                        <UnlockChoiceModalOptionIcon icon={option.icon} />
                    </span>

                    <span className={textsStyle}>
                        <span className={isExpanded ? expandedTitleSlotStyle : titleSlotStyle}>
                            <Text as="span" variant="headline_6" color="var(--colors-secondary)">
                                {option.title}
                            </Text>
                        </span>

                        {option.description && (
                            <span className={isExpanded ? expandedDescriptionStyle : descriptionStyle} aria-hidden={!isExpanded}>
                                <span className={descriptionClipStyle}>
                                    <Text as="span" variant="info_txt_1" color="var(--colors-gray-700)" className={descriptionTextStyle}>
                                        {option.description}
                                    </Text>
                                </span>
                            </span>
                        )}
                    </span>
                </span>

                <span className={isExpanded ? expandedRadioSlotStyle : radioSlotStyle} aria-hidden>
                    <span className={isSelected ? selectedRadioRingStyle : radioRingStyle}>{isSelected && <span className={radioDotStyle} />}</span>
                </span>
            </span>
        </label>
    );
};

const motionStyle = css.raw({
    transitionDuration: "250ms",
    transitionTimingFunction: "ease-out",
    _motionReduce: {
        transitionDuration: "0ms"
    }
});

const cardBaseStyle = css.raw({
    ...motionStyle,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: 1,
    cursor: "pointer",
    transitionProperty: "border-color, background-color, box-shadow",
    "&:has(input:focus-visible)": {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    },
    "&:has(input:disabled)": {
        cursor: "default"
    }
});

const cardStyle = css({
    ...cardBaseStyle,
    borderColor: "var(--colors-gray-400)",
    backgroundColor: "var(--colors-white)"
});

const selectedCardStyle = css({
    ...cardBaseStyle,
    borderColor: "var(--colors-secondary)",
    backgroundColor: "var(--colors-background-100)",
    boxShadow: "var(--shadow-deep)"
});

const radioInputStyle = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: "0"
});

const badgeBaseStyle = css.raw({
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-start",
    height: "2.2rem",
    px: 1,
    borderTopLeftRadius: 1,
    borderBottomRightRadius: 0.5
});

const greenBadgeStyle = css({
    ...badgeBaseStyle,
    backgroundColor: "var(--colors-success)"
});

const beigeBadgeStyle = css({
    ...badgeBaseStyle,
    backgroundColor: "var(--colors-background-200)"
});

const rowStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1.4rem",
    px: 2,
    py: 1.5
});

const bodyStyle = css({
    display: "flex",
    flex: "1 1 auto",
    alignItems: "flex-start",
    minWidth: 0,
    columnGap: 0.5
});

const iconTileStyle = css({
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "4rem",
    height: "4rem",
    borderRadius: "50%",
    backgroundColor: "var(--colors-beige-subtle)"
});

const textsStyle = css({
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    minWidth: 0
});

const titleSlotBaseStyle = css.raw({
    ...motionStyle,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    transitionProperty: "min-height"
});

const titleSlotStyle = css({
    ...titleSlotBaseStyle,
    minHeight: "4rem"
});

const expandedTitleSlotStyle = css({
    ...titleSlotBaseStyle,
    minHeight: "2rem",
    md: {
        minHeight: "2.4rem"
    }
});

const descriptionBaseStyle = css.raw({
    ...motionStyle,
    display: "grid",
    transitionProperty: "grid-template-rows, opacity, visibility"
});

const descriptionStyle = css({
    ...descriptionBaseStyle,
    gridTemplateRows: "0fr",
    opacity: 0,
    visibility: "hidden"
});

const expandedDescriptionStyle = css({
    ...descriptionBaseStyle,
    gridTemplateRows: "1fr",
    opacity: 1,
    visibility: "visible"
});

const descriptionClipStyle = css({
    minHeight: 0,
    overflow: "hidden"
});

const descriptionTextStyle = css({
    display: "block",
    pt: 0.5
});

const radioSlotBaseStyle = css.raw({
    ...motionStyle,
    display: "flex",
    flexShrink: 0,
    transitionProperty: "padding-top"
});

const radioSlotStyle = css({
    ...radioSlotBaseStyle,
    pt: 1
});

const expandedRadioSlotStyle = css({
    ...radioSlotBaseStyle,
    pt: 0
});

const radioRingBaseStyle = css.raw({
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "2rem",
    height: "2rem",
    margin: "0.2rem",
    borderWidth: "2px",
    borderStyle: "solid",
    borderRadius: "50%"
});

const radioRingStyle = css({
    ...radioRingBaseStyle,
    borderColor: "var(--colors-gray-700)"
});

const selectedRadioRingStyle = css({
    ...radioRingBaseStyle,
    borderColor: "var(--colors-secondary)"
});

const radioDotStyle = css({
    display: "block",
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
    backgroundColor: "var(--colors-secondary)"
});
